/**
 * ELM327 command driver.
 *
 * The adapter is strictly request/response: you write a command terminated by
 * CR, it streams back some lines, then a '>' prompt meaning it is ready for the
 * next one. Sending anything before that prompt confuses cheap clones badly, so
 * every command goes through a single-flight queue.
 */
import type { Transport } from '../types.js';
import { ObdError, cleanLines } from './parse.js';

export interface ElmResponse {
  command: string;
  /** Cleaned, uppercase, whitespace-free response lines with the echo removed. */
  lines: string[];
  raw: string;
  elapsedMs: number;
}

export interface AdapterInfo {
  /** Whatever ATI reports, e.g. "ELM327 v1.5". Clones lie, but it is a clue. */
  identifier: string;
  /** Human-readable protocol name from ATDP. */
  protocol: string;
  /** Protocol number from ATDPN. 6..9 and A..C are the CAN variants. */
  protocolNumber: number;
  isCan: boolean;
  /** Whether the vehicle answered a multi-PID request correctly. */
  supportsBatch: boolean;
  /**
   * Whether a reply longer than one CAN frame (7 data bytes) can be received.
   *
   * This needs the adapter to complete the ISO-TP flow-control handshake. Many
   * clones manage it for a plain request but fail once the expected-responses
   * hint is appended, so it is measured rather than assumed.
   *
   * Measuring it needs PIDs the car actually answers for, which is not known
   * until discovery has run, so `initialise` leaves this false and the session
   * fills it in afterwards.
   */
  supportsMultiFrame: boolean;
  /** Battery voltage at the OBD port, from ATRV. */
  voltage?: number;
}

export interface Elm327Options {
  /** Milliseconds to wait for a prompt before giving up. */
  defaultTimeoutMs?: number;
  /** Called with every command and response, for the raw terminal view. */
  onTrace?: (direction: 'tx' | 'rx', text: string) => void;
}

interface Pending {
  command: string;
  resolve: (r: ElmResponse) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  started: number;
}

const CR = 0x0d;

export class Elm327 {
  private buffer = '';
  private pending: Pending | null = null;
  private chain: Promise<unknown> = Promise.resolve();
  private unsubscribe: (() => void) | null = null;
  private readonly encoder = new TextEncoder();
  private readonly decoder = new TextDecoder();
  private readonly timeoutMs: number;
  private readonly onTrace: ((d: 'tx' | 'rx', t: string) => void) | undefined;

  constructor(private readonly transport: Transport, opts: Elm327Options = {}) {
    this.timeoutMs = opts.defaultTimeoutMs ?? 5000;
    this.onTrace = opts.onTrace;
  }

  attach(): void {
    this.unsubscribe?.();
    this.unsubscribe = this.transport.onData((chunk) => this.ingest(chunk));
  }

  detach(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.failPending(new ObdError('Adapter detached', 'adapter'));
  }

  private ingest(chunk: Uint8Array): void {
    this.buffer += this.decoder.decode(chunk, { stream: true });
    // '>' is the only reliable end-of-response marker; line counts vary.
    const promptAt = this.buffer.indexOf('>');
    if (promptAt === -1) return;

    const raw = this.buffer.slice(0, promptAt);
    this.buffer = this.buffer.slice(promptAt + 1);
    this.complete(raw);
  }

  private complete(raw: string): void {
    const p = this.pending;
    if (!p) return; // unsolicited output, e.g. the banner after a reset
    this.pending = null;
    clearTimeout(p.timer);

    const echo = p.command.replace(/\s+/g, '').toUpperCase();
    const lines = cleanLines(raw).filter((l) => l !== echo);
    this.onTrace?.('rx', raw.trim());
    p.resolve({ command: p.command, lines, raw, elapsedMs: Date.now() - p.started });
  }

  private failPending(err: Error): void {
    const p = this.pending;
    if (!p) return;
    this.pending = null;
    clearTimeout(p.timer);
    p.reject(err);
  }

  /** Queue a command. Resolves once the adapter returns its prompt. */
  send(command: string, timeoutMs?: number): Promise<ElmResponse> {
    const run = (): Promise<ElmResponse> => this.exec(command, timeoutMs ?? this.timeoutMs);
    const p = this.chain.then(run, run);
    // Keep the chain alive even when one command fails.
    this.chain = p.then(
      () => undefined,
      () => undefined,
    );
    return p;
  }

  private exec(command: string, timeoutMs: number): Promise<ElmResponse> {
    if (!this.transport.connected) {
      return Promise.reject(new ObdError('Not connected', 'adapter'));
    }
    return new Promise<ElmResponse>((resolve, reject) => {
      this.buffer = '';
      const timer = setTimeout(() => {
        this.pending = null;
        reject(new ObdError('Timed out waiting for "' + command + '"', 'timeout'));
      }, timeoutMs);

      this.pending = { command, resolve, reject, timer, started: Date.now() };
      this.onTrace?.('tx', command);

      const bytes = this.encoder.encode(command);
      const frame = new Uint8Array(bytes.length + 1);
      frame.set(bytes);
      frame[bytes.length] = CR;

      this.transport.write(frame).catch((e: unknown) => {
        clearTimeout(timer);
        this.pending = null;
        reject(e instanceof Error ? e : new Error(String(e)));
      });
    });
  }

  /**
   * Bring the adapter up and work out what it can do.
   *
   * Echo, linefeeds and spaces all get turned off because every byte they add
   * is bandwidth we would rather spend on data. Adaptive timing plus a modest
   * ceiling gives the fastest turnaround that still tolerates a slow ECU.
   */
  async initialise(): Promise<AdapterInfo> {
    // A warm start is quicker than ATZ, but not every clone implements it.
    try {
      await this.send('ATWS', 3000);
    } catch {
      await this.send('ATZ', 6000);
    }

    let identifier = 'unknown';
    try {
      const id = await this.send('ATI', 3000);
      identifier = id.lines.join(' ') || 'unknown';
    } catch {
      /* non-fatal */
    }

    await this.sendQuiet('ATE0'); // echo off
    await this.sendQuiet('ATL0'); // no linefeeds
    await this.sendQuiet('ATS0'); // no spaces in responses
    await this.sendQuiet('ATH0'); // no headers
    await this.sendQuiet('ATAT2'); // aggressive adaptive timing
    await this.sendQuiet('ATST20'); // 32 * 4ms ceiling before "NO DATA"
    await this.sendQuiet('ATSP0'); // let the adapter negotiate the protocol

    // The first real request is what actually triggers the protocol search, and
    // it can take several seconds on a bus the adapter has not seen before.
    await this.send('0100', 12000);

    let protocolNumber = 0;
    let protocol = 'unknown';
    try {
      const dpn = await this.send('ATDPN', 3000);
      const raw = (dpn.lines[0] ?? '').replace(/^A/, '');
      protocolNumber = parseInt(raw, 16);
      if (Number.isNaN(protocolNumber)) protocolNumber = 0;
    } catch {
      /* non-fatal */
    }
    try {
      const dp = await this.send('ATDP', 3000);
      protocol = dp.lines.join(' ') || 'unknown';
    } catch {
      /* non-fatal */
    }

    let voltage: number | undefined;
    try {
      const rv = await this.send('ATRV', 3000);
      const v = parseFloat((rv.lines[0] ?? '').replace(/V$/i, ''));
      if (!Number.isNaN(v)) voltage = v;
    } catch {
      /* non-fatal */
    }

    // Protocols 6-9 and A-C are the CAN family; only those do multi-PID well.
    const isCan = protocolNumber >= 6 && protocolNumber <= 0xc;
    const supportsBatch = isCan ? await this.probeBatch() : false;

    // Multi-frame cannot be probed here: it needs a reply over seven bytes
    // long, which means knowing which PIDs this car answers for. The session
    // measures it once discovery has produced that list.
    return { identifier, protocol, protocolNumber, isCan, supportsBatch, supportsMultiFrame: false, voltage };
  }

  /** Send a setup command, tolerating adapters that answer '?' to extras. */
  private async sendQuiet(cmd: string): Promise<void> {
    try {
      await this.send(cmd, 3000);
    } catch {
      /* an unsupported AT command is not worth aborting the session for */
    }
  }

  /**
   * Ask for RPM and speed in one request and see whether both come back.
   *
   * This is the single biggest throughput win available -- six PIDs per round
   * trip instead of one -- but support depends on the vehicle as much as the
   * adapter, so it has to be measured rather than assumed.
   */
  private async probeBatch(): Promise<boolean> {
    try {
      const r = await this.send('010C0D', 4000);
      const joined = r.lines.join('');
      // A successful batch reply contains both PID markers after the 0x41.
      return joined.startsWith('410C') && joined.includes('0D') && joined.length >= 12;
    } catch {
      return false;
    }
  }

  /** Read the port voltage without disturbing the polling loop's assumptions. */
  async readVoltage(): Promise<number | undefined> {
    try {
      const rv = await this.send('ATRV', 2000);
      const v = parseFloat((rv.lines[0] ?? '').replace(/V$/i, ''));
      return Number.isNaN(v) ? undefined : v;
    } catch {
      return undefined;
    }
  }
}
